/**
 * Утилита для получения заголовков авторизации
 * Поддерживает Telegram WebApp, Login Widget (веб-версия) и Session-based auth (Google/Yandex OAuth)
 */

/**
 * Получить заголовки авторизации для fetch запросов
 * Приоритет: WebApp.initData > localStorage telegram_auth_data > session_id
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  // 1. Telegram WebApp (Mini App) — высший приоритет
  const webAppData = (window as any).Telegram?.WebApp?.initData;
  if (typeof webAppData === 'string' && webAppData.length > 0) {
    headers['X-Telegram-Init-Data'] = webAppData;
    headers['X-Platform-Type'] = 'Telegram APP';
    return headers;
  }

  // 2. Telegram Login Widget из localStorage
  const storedAuth = localStorage.getItem('telegram_auth_data');
  if (typeof storedAuth === 'string' && storedAuth.length > 0) {
    headers['X-Telegram-Init-Data'] = storedAuth;
    headers['X-Platform-Type'] = 'WEB';
    return headers;
  }

  // 3. Session-based auth (Google, Yandex OAuth)
  const sessionId = localStorage.getItem('session_id');
  if (typeof sessionId === 'string' && sessionId.length > 0) {
    headers['X-Session-ID'] = sessionId;
    headers['X-Platform-Type'] = 'WEB';
    return headers;
  }

  return headers;
}

/**
 * Проверить доступность данных авторизации
 */
export function hasAuthData(): boolean {
  const webAppData = (window as any).Telegram?.WebApp?.initData;
  const storedAuth = localStorage.getItem('telegram_auth_data');
  const sessionId = localStorage.getItem('session_id');

  const hasWebAppData = typeof webAppData === 'string' && webAppData.length > 0;
  const hasStoredAuth = typeof storedAuth === 'string' && storedAuth.length > 0;
  const hasSessionId = typeof sessionId === 'string' && sessionId.length > 0;

  return hasWebAppData || hasStoredAuth || hasSessionId;
}

/**
 * Получить тип текущей авторизации
 */
export function getAuthType(): 'webapp' | 'login_widget' | 'session' | null {
  const storedAuth = localStorage.getItem('telegram_auth_data');
  const webAppData = (window as any).Telegram?.WebApp?.initData;
  const sessionId = localStorage.getItem('session_id');

  // WebApp имеет приоритет
  if (webAppData && !storedAuth) return 'webapp';

  // Если есть данные в localStorage - это Login Widget
  if (storedAuth) return 'login_widget';

  // Session-based auth (Google, Yandex)
  if (sessionId) return 'session';

  return null;
}

/**
 * Проверить, запущено ли приложение внутри Telegram Mini App
 * Используется для определения: отправлять файлы в чат или скачивать в браузер
 */
export function isInMiniApp(): boolean {
  // Используем класс is-telegram который устанавливается в index.html
  // Это самый надёжный способ определения - класс ставится ДО загрузки React
  if (document.documentElement.classList.contains('is-telegram')) {
    return true;
  }
  if (document.documentElement.classList.contains('is-website')) {
    return false;
  }

  // Fallback: проверяем наличие Telegram WebApp API и реальных initData
  const webApp = (window as any).Telegram?.WebApp;

  // Если нет WebApp или initData пустой - мы в браузере
  if (!webApp?.initData || webApp.initData.length === 0) return false;

  // Если есть localStorage данные от Login Widget - мы в браузере
  // (даже если WebApp API частично доступен)
  const storedAuth = localStorage.getItem('telegram_auth_data');
  if (storedAuth) return false;

  // Проверяем platform - только конкретные Telegram платформы
  const telegramPlatforms = ['android', 'ios', 'android_x', 'tdesktop', 'macos', 'weba', 'webk', 'unigram'];
  if (!webApp.platform || !telegramPlatforms.includes(webApp.platform)) return false;

  return true;
}

/**
 * Получить initData как строку (для WebSocket, SSE и др.)
 * Для Telegram возвращает initData, для Session auth — session_id
 * Возвращает пустую строку если данных нет
 */
export function getInitData(): string {
  const webAppData = (window as any).Telegram?.WebApp?.initData;
  if (typeof webAppData === 'string' && webAppData.length > 0) {
    return webAppData;
  }

  const storedAuth = localStorage.getItem('telegram_auth_data');
  if (storedAuth) return storedAuth;

  // Для Session-based auth (Google, Yandex) — возвращаем session_id
  // SSE/WebSocket могут использовать его как query-параметр
  return localStorage.getItem('session_id') || '';
}

/**
 * Очистить данные авторизации (для logout)
 */
export function clearAuthData(): void {
  localStorage.removeItem('telegram_auth_data');
  localStorage.removeItem('session_id');
  localStorage.removeItem('google_user_data');
}
